import { useState } from 'react'
import {
  App, Button, DatePicker, Form, Input, InputNumber, Modal, Select, Space,
  Switch, Tag, Typography,
} from 'antd'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'

import { utilityLogsApi } from '@/features/maintenance/api'
import DataTable from '@/shared/components/DataTable'
import PageHeader from '@/shared/components/PageHeader'
import { usePermission } from '@/shared/hooks/usePermission'
import { usePlantId } from '@/shared/hooks/usePlantId'
import type { UtilityLog } from '@/shared/types/maintenance'

const { Text } = Typography

const UTILITY_TYPES = [
  'COMPRESSOR', 'HUMIDIFICATION', 'DG_SET', 'ELECTRICAL_PANEL',
  'CHILLER', 'BOILER', 'WATER',
]

/**
 * Spec §13 — Utility Running Log.
 * Quick fast-entry form for compressor / humidification / DG / etc readings.
 * Marking abnormality=Yes requires text + suggests creating a follow-up WO.
 */
export default function UtilityRunningLogPage() {
  const { message } = App.useApp()
  const qc = useQueryClient()
  const { plantId } = usePlantId()
  const canCreate = usePermission('maintenance.create')

  const [createOpen, setCreateOpen] = useState(false)
  const [filters, setFilters] = useState<{ utility_type?: string; abnormalOnly?: boolean }>({})

  const { data: logs, isLoading } = useQuery({
    queryKey: ['utility-logs', plantId, filters],
    enabled: !!plantId,
    queryFn: () => utilityLogsApi.list({
      plant_id: plantId,
      utility_type: filters.utility_type,
      abnormal_only: filters.abnormalOnly,
      limit: 200,
    }),
  })

  const [form] = Form.useForm()
  const abnormality = Form.useWatch('abnormality', form)
  const createMutation = useMutation({
    mutationFn: (vals: {
      utility_type: string
      log_date: dayjs.Dayjs
      running_hours?: number
      pressure?: number
      temperature?: number
      humidity?: number
      power_consumption_kwh?: number
      abnormality?: boolean
      abnormality_text?: string
      action_taken?: string
    }) => utilityLogsApi.create({
      plant_id: plantId,
      utility_type: vals.utility_type,
      log_date: vals.log_date.format('YYYY-MM-DD'),
      running_hours: vals.running_hours,
      pressure: vals.pressure,
      temperature: vals.temperature,
      humidity: vals.humidity,
      power_consumption_kwh: vals.power_consumption_kwh,
      abnormality: !!vals.abnormality,
      abnormality_text: vals.abnormality_text,
      action_taken: vals.action_taken,
    }),
    onSuccess: () => {
      message.success('Utility log saved')
      qc.invalidateQueries({ queryKey: ['utility-logs'] })
      setCreateOpen(false)
    },
    onError: (e) => message.error((e as { message?: string })?.message ?? 'Save failed'),
  })

  const columns = [
    {
      title: 'Date', dataIndex: 'log_date', key: 'date', width: 120,
      render: (v: string) => dayjs(v).format('DD MMM YYYY'),
    },
    { title: 'Utility', dataIndex: 'utility_type', key: 'ut', width: 160 },
    {
      title: 'Status', dataIndex: 'status', key: 'st', width: 110,
      render: (v: string, r: UtilityLog) => (
        <Space>
          <Tag color={v === 'OK' ? 'green' : 'red'}>{v}</Tag>
          {r.abnormality && <Tag color="orange">ABNORMAL</Tag>}
        </Space>
      ),
    },
    {
      title: 'Running h', dataIndex: 'running_hours', key: 'rh', width: 100,
      render: (v: string | number | null) => v != null ? Number(v).toFixed(1) : '—',
    },
    {
      title: 'Pressure', dataIndex: 'pressure', key: 'pr', width: 90,
      render: (v: string | number | null) => v != null ? Number(v).toFixed(2) : '—',
    },
    {
      title: 'Temp', dataIndex: 'temperature', key: 'temp', width: 80,
      render: (v: string | number | null) => v != null ? `${Number(v).toFixed(1)}°` : '—',
    },
    {
      title: 'Humidity', dataIndex: 'humidity', key: 'hum', width: 90,
      render: (v: string | number | null) => v != null ? `${Number(v).toFixed(1)}%` : '—',
    },
    {
      title: 'Power kWh', dataIndex: 'power_consumption_kwh', key: 'pw', width: 110,
      render: (v: string | number | null) => v != null ? Number(v).toFixed(2) : '—',
    },
    { title: 'Abnormality', dataIndex: 'abnormality_text', key: 'at', ellipsis: true },
    { title: 'Action', dataIndex: 'action_taken', key: 'ac', ellipsis: true },
  ]

  return (
    <div>
      <PageHeader
        title="Utility Running Log"
        subtitle="Compressor • Humidification • DG • Electrical • Chiller readings."
        extra={canCreate && (
          <Button type="primary" onClick={() => {
            form.resetFields()
            form.setFieldsValue({ log_date: dayjs(), abnormality: false })
            setCreateOpen(true)
          }}>New entry</Button>
        )}
      />

      <Space style={{ marginBottom: 12 }} wrap>
        <Select
          allowClear placeholder="Utility filter" style={{ width: 200 }}
          options={UTILITY_TYPES.map((u) => ({ label: u, value: u }))}
          onChange={(v) => setFilters((f) => ({ ...f, utility_type: v }))}
        />
        <Switch
          checkedChildren="Abnormal only" unCheckedChildren="All"
          checked={!!filters.abnormalOnly}
          onChange={(v) => setFilters((f) => ({ ...f, abnormalOnly: v }))}
        />
      </Space>

      <DataTable rowKey="id" columns={columns} dataSource={logs} loading={isLoading} />

      <Modal
        title="New utility log"
        open={createOpen}
        onOk={() => form.validateFields().then((v) => createMutation.mutate(v))}
        onCancel={() => setCreateOpen(false)}
        confirmLoading={createMutation.isPending}
        width={620}
      >
        <Form form={form} layout="vertical">
          <Form.Item name="utility_type" label="Utility" rules={[{ required: true }]}>
            <Select options={UTILITY_TYPES.map((u) => ({ label: u, value: u }))} />
          </Form.Item>
          <Form.Item name="log_date" label="Date" rules={[{ required: true }]}>
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
          <Space wrap style={{ width: '100%' }}>
            <Form.Item name="running_hours" label="Running hours">
              <InputNumber min={0} step={0.5} style={{ width: 140 }} />
            </Form.Item>
            <Form.Item name="pressure" label="Pressure (bar)">
              <InputNumber min={0} step={0.01} style={{ width: 140 }} />
            </Form.Item>
            <Form.Item name="temperature" label="Temperature (°C)">
              <InputNumber step={0.1} style={{ width: 140 }} />
            </Form.Item>
            <Form.Item name="humidity" label="Humidity (%)">
              <InputNumber min={0} max={100} step={0.1} style={{ width: 140 }} />
            </Form.Item>
            <Form.Item name="power_consumption_kwh" label="Power (kWh)">
              <InputNumber min={0} step={0.1} style={{ width: 160 }} />
            </Form.Item>
          </Space>
          <Form.Item name="abnormality" label="Abnormality?" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item
            name="abnormality_text" label="Abnormality details"
            rules={[
              {
                validator: (_, v) => abnormality && !v
                  ? Promise.reject(new Error('Required when abnormality=Yes'))
                  : Promise.resolve(),
              },
            ]}>
            <Input.TextArea rows={2} disabled={!abnormality} />
          </Form.Item>
          <Form.Item name="action_taken" label="Action taken">
            <Input.TextArea rows={2} />
          </Form.Item>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Marking abnormality = Yes will flag this entry; supervisor can later create a follow-up WO from the alerts board.
          </Text>
        </Form>
      </Modal>
    </div>
  )
}
